import type { ContactCandidate } from "@shared/lib/leads-shared";
import type { MessageRecord, ReplyRecord } from "@shared/lib/prospection/shared";

/**
 * Types et constantes de la base de prospection (migrations 015, 016 et 021),
 * partagés entre le serveur (lib/admin-prospects.ts) et les composants client.
 * Ce module ne doit importer aucune dépendance serveur (db,
 * logger…) — même rôle que lib/leads-shared.ts pour le pipeline de leads.
 */

export const PROSPECT_STATUSES = [
  "new",
  "to_contact",
  "contacted",
  "finished",
  "replied",
  "later",
  "won",
  "dismissed",
  "unsubscribed",
  "invalid",
] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];

export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  new: "Nouveau",
  to_contact: "À contacter",
  contacted: "Contacté",
  finished: "Séquence terminée",
  replied: "A répondu",
  later: "Plus tard",
  won: "Client",
  dismissed: "Écarté",
  unsubscribed: "Désinscrit",
  invalid: "Adresse invalide",
};

/** Teinte du point de statut, du plus engagé au plus fermé. */
export const PROSPECT_STATUS_TONES: Record<ProspectStatus, string> = {
  new: "neutral",
  to_contact: "info",
  contacted: "trial",
  finished: "neutral",
  replied: "active",
  later: "trial",
  won: "active",
  dismissed: "neutral",
  unsubscribed: "churned",
  invalid: "churned",
};

/** Statuts où la séquence peut encore envoyer quelque chose. */
export const IN_SEQUENCE_STATUSES: ProspectStatus[] = ["to_contact", "contacted"];

export type ProspectResearchStatus = "idle" | "running" | "completed" | "failed";

/**
 * Au-delà de cette durée, une passe encore marquée « en cours » est considérée
 * comme interrompue (fonction serveur coupée) : la fiche cesse d'attendre et
 * l'analyse redevient relançable.
 */
export const PROSPECT_RESEARCH_TIMEOUT_MS = 15 * 60 * 1000;

export function isResearchStale(prospect: {
  research_status: ProspectResearchStatus;
  research_started_at: string | null;
}) {
  if (prospect.research_status !== "running") return false;
  if (!prospect.research_started_at) return true;
  return Date.now() - new Date(prospect.research_started_at).getTime() > PROSPECT_RESEARCH_TIMEOUT_MS;
}

/** Nom provisoire d'un prospect créé à partir de son seul site : « cabinet-durand.fr » → « Cabinet durand ». */
export function nameFromWebsite(website: string | null | undefined): string | null {
  if (!website) return null;
  try {
    const host = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`).hostname.replace(/^www\./, "");
    const label = (host.split(".")[0] ?? "").replace(/[-_]+/g, " ").trim();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : null;
  } catch {
    return null;
  }
}

/**
 * Un membre du comité d'approche trouvé par l'agent chez le prospect, pour
 * l'offre Hune : mêmes champs qu'un interlocuteur de lead, pour être montré
 * par les mêmes composants (shared/components/contact.tsx).
 */
export interface ProspectCommitteeMember {
  id: string;
  priority_rank: number;
  contact_name: string;
  contact_role: string | null;
  decision_role: ContactCandidate["decision_role"];
  confidence: ContactCandidate["confidence"];
  contact_linkedin_url: string | null;
  contact_photo_url: string | null;
  contact_reason: string | null;
  outreach_angle: string | null;
  sources: string[];
}

export interface ProspectRecord {
  id: string;
  name: string;
  website: string | null;
  contact_name: string | null;
  contact_role: string | null;
  contact_email: string | null;
  contact_linkedin_url: string | null;
  contact_photo_url: string | null;
  /** Pourquoi l'agent a retenu cette personne (null pour un contact saisi). */
  contact_reason: string | null;
  contact_origin: "manual" | "agent" | null;
  /** Le comité d'approche trouvé par l'agent, interlocuteur retenu compris. */
  contact_committee: ProspectCommitteeMember[];
  contact_researched_at: string | null;
  status: ProspectStatus;
  source: string | null;
  notes: string | null;
  offer_name: string | null;
  offer_summary: string | null;
  priority_segment: string | null;
  decision_maker: string | null;
  favorable_event: string | null;
  exclusions: string | null;
  offer_edited_at: string | null;
  research_status: ProspectResearchStatus;
  research_error: string | null;
  research_started_at: string | null;
  researched_at: string | null;
  research_summary: string | null;
  contacted_at: string | null;
  auto_enabled: boolean;
  last_touch_at: string | null;
  touch_count: number;
  next_eligible_at: string | null;
  contact_key: string | null;
  public_token: string;
  mailbox_id: string | null;
  thread_id: string | null;
  sequence_step: number;
  last_contacted_at: string | null;
  next_step_on: string | null;
  replied_at: string | null;
  unsubscribed_at: string | null;
  /** Dernière ouverture du dossier par un administrateur. */
  opened_at: string | null;
  /** Recherche de l'adresse par FullEnrich (prospection/email-finder.ts). */
  email_lookup_status: EmailLookupStatus | null;
  email_lookup_at: string | null;
  email_lookup_error: string | null;
  created_at: string;
  updated_at: string;
}

export type EmailLookupStatus = "running" | "found" | "not_found" | "failed";

/** Analysé par l'agent depuis la dernière ouverture du dossier. */
export function isUnopened(prospect: { researched_at: string | null; opened_at: string | null }) {
  if (!prospect.researched_at) return false;
  return !prospect.opened_at || new Date(prospect.opened_at) < new Date(prospect.researched_at);
}

export interface ProspectLeadRecord {
  id: string;
  prospect_id: string;
  organization: string;
  category: string | null;
  readiness: string | null;
  priority: number | null;
  market_clue: string | null;
  source_summary: string | null;
  relevance: string | null;
  momentum: string | null;
  source_name: string | null;
  source_link: string | null;
  source_format: string | null;
  observed_on: string | null;
  contact_route: string | null;
  verification_note: string | null;
  assessment: Record<string, number> | null;
  selected: boolean;
  /**
   * Relance déjà rédigée pour livrer ce signal (liste préparée,
   * lib/prospection/prepared-lists.ts) : le signal et son lien, sans formule
   * d'appel ni question, que le code ajoute. Nul : la relance se rédige.
   */
  email_body?: string | null;
  email_angle?: string | null;
  created_at: string;
}

export interface ProspectListItem extends ProspectRecord {
  lead_count: number;
  selected_lead: Pick<ProspectLeadRecord, "id" | "organization" | "priority"> | null;
}

export interface ProspectDetail extends ProspectRecord {
  leads: ProspectLeadRecord[];
  messages: MessageRecord[];
  replies: ReplyRecord[];
}

// ---------------------------------------------------------------------------
// File de préparation (migration 016)
// ---------------------------------------------------------------------------

export type ProspectTouchStatus = "prepared" | "skipped" | "failed";

export type ProspectionQueueStats = {
  /** Prospects immédiatement éligibles au prochain passage. */
  queued: number;
  /** Préparations sur les 24 dernières heures (quota consommé). */
  usedToday: number;
  lastTouchAt: string | null;
};

// ---------------------------------------------------------------------------
// Étapes de travail (/admin/prospection)
//
// Le prospect porte trois états à la fois (statut, analyse, e-mail en
// attente) ; l'écran n'en montre qu'un, l'étape, qui dit ce qu'il attend de
// l'administrateur : trier, laisser l'agent travailler, compléter et valider,
// laisser partir, suivre.
// ---------------------------------------------------------------------------

export const PROSPECT_STAGES = ["triage", "queue", "complete", "ready", "sequence", "closed"] as const;
export type ProspectStage = (typeof PROSPECT_STAGES)[number];

export const PROSPECT_STAGE_LABELS: Record<ProspectStage, string> = {
  triage: "À trier",
  queue: "En analyse",
  complete: "À vérifier",
  ready: "Prêts à partir",
  sequence: "En cours",
  closed: "Clos",
};

/** Ce que l'étape attend de l'administrateur, sous son effectif. */
export const PROSPECT_STAGE_HINTS: Record<ProspectStage, string> = {
  triage: "À cocher pour l’agent",
  queue: "L’agent travaille",
  complete: "La personne et l’e-mail",
  ready: "Partent au prochain créneau",
  sequence: "Relances et réponses",
  closed: "Terminés, clients, écartés",
};

/** Le premier e-mail en attente d'un prospect, tel que l'étape le lit. */
export interface ProspectBoardMessage {
  id: string;
  kind: "sequence" | "reply";
  rank: number | null;
  status: "to_review" | "ready" | "held" | "sending" | "sent" | "failed" | "cancelled";
  subject: string | null;
  body: string;
  hold_reason: string | null;
  error: string | null;
  guard_verdict: "pass" | "hold" | null;
  /** Le signal que l'e-mail livre. */
  signal: { organization: string; priority: number | null; market_clue: string | null; source_link: string | null } | null;
  created_at: string;
}

export function prospectStage(
  prospect: Pick<ProspectRecord, "status" | "auto_enabled" | "contact_email" | "research_status" | "research_started_at">,
  pending: Pick<ProspectBoardMessage, "kind" | "rank" | "status"> | null,
): ProspectStage {
  switch (prospect.status) {
    case "new":
      if (prospect.research_status === "running" && !isResearchStale(prospect)) return "queue";
      return prospect.auto_enabled ? "queue" : "triage";
    case "to_contact": {
      const firstReady = pending?.kind === "sequence" && (pending.rank ?? 1) === 1 && pending.status === "ready";
      return firstReady && prospect.contact_email?.trim() ? "ready" : "complete";
    }
    case "contacted":
    case "replied":
    case "later":
      return "sequence";
    default:
      return "closed";
  }
}

/** Actions groupées de la liste (POST /api/admin/prospection/bulk). */
export const PROSPECT_BULK_ACTIONS = ["queue", "unqueue", "analyze", "dismiss", "delete"] as const;
export type ProspectBulkAction = (typeof PROSPECT_BULK_ACTIONS)[number];

/** Au plus autant d'analyses lancées d'un coup hors de la file (coût du modèle). */
export const MAX_IMMEDIATE_ANALYSES = 10;

export interface ProspectBoardItem {
  id: string;
  name: string;
  website: string | null;
  contact_name: string | null;
  contact_role: string | null;
  contact_email: string | null;
  contact_photo_url: string | null;
  contact_linkedin_url: string | null;
  /** Pourquoi l'agent a retenu cette personne. */
  contact_reason: string | null;
  /** Personnes du comité d'approche, la retenue comprise. */
  committee_size: number;
  status: ProspectStatus;
  stage: ProspectStage;
  offer_name: string | null;
  research_status: ProspectResearchStatus;
  research_error: string | null;
  selected_lead: { organization: string; priority: number | null } | null;
  lead_count: number;
  /** Premier e-mail ou réponse en attente, corps compris pour la relecture. */
  pending: ProspectBoardMessage | null;
  /** Ce que la ligne dit de la suite, calculé côté serveur (dates de Paris). */
  next_label: string | null;
  sequence_step: number;
  /** Analysé par l'agent depuis la dernière ouverture du dossier. */
  unopened: boolean;
  /** Recherche de l'adresse par FullEnrich, si elle a été lancée. */
  email_lookup: { status: EmailLookupStatus; error: string | null } | null;
  created_at: string;
  updated_at: string;
}
